import {Component, OnDestroy, OnInit} from '@angular/core';
import {selectCompanyLoading, selectCompanyPage} from '../../store/company/selectors/company.selectors';
import {Store} from '@ngrx/store';
import {CompanyState} from '../../store/company/reducers/company.reducer';
import {combineLatestWith, filter, map, Observable, Subject, takeUntil} from 'rxjs';
import {CompanyPage} from '../../model/company-page';
import {AuthState} from '../../store/auth/reducers/auth.reducer';
import {selectUserRoles} from '../../store/auth/selectors/auth.selectors';
import {Roles} from '../../enum/roles';
import {UserAppState} from '../../store/user/reducers/user.reducer';
import {selectUser} from '../../store/user/selectors/user.selectors';
import {CompanyService} from '../../services/company-service/company.service';
import {CompanyPageActions} from '../../store/company/actions/company.actions';
import {Route} from '../../enum/route';
import {ActivatedRoute, Router} from '@angular/router';
import {CustomDialogService} from '../../services/custom-dialog/custom-dialog.service';
import {UserService} from '../../services/user/user.service';
import {environment} from 'src/environments/environment';
import {User} from '../../model/user';
import {UserPage} from '../../model/user-page';

@Component({
    selector: 'app-company-profile',
    templateUrl: './profile-company.component.html',
    styleUrls: ['../../../styles/profile.scss', './profile-company.component.scss'],
    standalone: false
})
export class ProfileCompanyComponent implements OnInit, OnDestroy {
  protected destroy$ = new Subject<void>();
  loading$ = this.companyStore.select(selectCompanyLoading);
  companyPage$: Observable<CompanyPage> = this.companyStore.select(selectCompanyPage);
  pageId: string;

  isPageAdmin: boolean = false;
  showEditField: boolean;
  logo;
  titlePicture;
  companyRole;
  userRoles: Roles[] = [];
  maxLobShown = 3;
  showCompleteIntroductionText: boolean = false;
  editImage: boolean = false;
  environment = environment;

  activeUser$ = this.userStore.select(selectUser);

  constructor(private readonly companyStore: Store<CompanyState>,
              private readonly authStore: Store<AuthState>,
              private readonly userStore: Store<UserAppState>,
              readonly companyService: CompanyService,
              private readonly activatedRoute: ActivatedRoute,
              private readonly router: Router,
              readonly customDialogService: CustomDialogService,
              public readonly userService: UserService,
  ) {
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      if (params['id'] == null) {
        this.router.navigate(['/' + Route.INSERATE]).then();
      } else {
        this.pageId = params['id'];
        this.reloadInformation();
      }
    });
    const isCorporateAdmin$ = this.authStore.select(selectUserRoles).pipe(map(roles => roles.includes(Roles.ROLE_CORPORATE_ADMIN)));
    const isDirectCorporateAdmin$ = this.authStore.select(selectUserRoles).pipe(map(roles => roles.includes(Roles.ROLE_DIRECT_CORPORATE_ADMIN)));
    this.companyPage$.pipe(
      filter(companyPage => companyPage != null),
      combineLatestWith(this.userStore.select(selectUser), isCorporateAdmin$, isDirectCorporateAdmin$),
      map(([companyPage, user, isAdmin, isAdminOnly]) => {
        return isAdminOnly || (companyPage.companyId == user.company?.companyId && isAdmin);
      }),
      takeUntil(this.destroy$)
    ).subscribe(
      isPageAdmin => this.isPageAdmin = isPageAdmin
    );
    this.userStore.select(selectUserRoles).subscribe(roles => this.userRoles = roles);
  }

  onFileChange(event, type: string) {
    let file = event.target.files[0];
    if (file.size > 2e+6) {
      this.customDialogService.imageUploadError();
      return;
    }
    if (type === 'logo') {
      this.companyService.setLogo(this.pageId, file).subscribe(_ => this.reloadInformation());
    } else if (type === 'titlePicture') {
      this.companyService.setTitlePicture(this.pageId, file).subscribe(_ => this.reloadInformation());
    }
  }

  reloadInformation() {
    this.companyService.getLogo(this.pageId)
      .subscribe(v => this.logo = v);
    this.companyService.getTitlePicture(this.pageId)
      .subscribe(v => this.titlePicture = v);
    const companyId = this.pageId;
    this.companyStore.dispatch(CompanyPageActions.load({companyId}));
    this.showEditField = false;
    this.editImage = false;
    this.companyService.getCompanyRole(companyId)
      .subscribe(companyRole => {
        this.companyRole = companyRole.companyRole;
      });
  }

  toggleMaxLobShow() {
    if (this.maxLobShown !== 3) {
      this.maxLobShown = 3;
    } else {
      this.maxLobShown = Number.MAX_SAFE_INTEGER;
    }
  }

  navigateToUserProfile(userId) {
    this.router.navigate([Route.USER_PAGE, userId]);
  }

  toggleCompleteIntroductionText() {
    this.showCompleteIntroductionText = !this.showCompleteIntroductionText;
  }

  deleteLogo() {
    this.companyService.deleteLogo(this.pageId).subscribe(_ => this.reloadInformation());
  }

  protected readonly Roles = Roles;

  hasExchangeRole(users: User[]) {
    return users.filter(user => user.type == Roles.ROLE_BROKER ||  user.type == Roles.ROLE_INSURER || user.type == Roles.ROLE_UNDERWRITER || user.type === Roles.ROLE_EXPERT).length > 0;
  }

  hasRepresentativeRole(users: User[]) {
    return users.filter(user => user.isRepresentative).length > 0;
  }

  hasCorporateAdminRole(users: User[]) {
    return users.filter(user => user.isCorporateAdmin).length > 0;
  }

  belongsToCompany(users: User[], userPage: UserPage) {
    return users.some(user => user.id === userPage.id);
  }

  hasAdminRole(userPage: UserPage | null) {
    return userPage.type === Roles.ROLE_ADMIN || userPage.type === Roles.ROLE_ASSOCIATION_ADMIN;
  }
}
